import { useMemo, useState } from "react";
import { ArrowRight, SlidersHorizontal } from "lucide-react";
import { Slider } from "../ui/slider";
import { useRfq } from "./RfqContext";

const DEFAULT_MATERIALS = ["NBR", "EPDM", "Neoprene (CR)", "Silicone", "Natural Rubber"];
const VOLUMES = ["1,000", "5,000", "10,000", "25,000", "50,000+"];
const COLOURS = ["Black", "Other (specify)"];

const parseMaterials = (specs) => {
  const m = (specs || []).find((s) => /material/i.test(s?.label || ""));
  const list = m?.value ? m.value.split(",").map((x) => x.trim()).filter(Boolean) : [];
  return list.length ? list : DEFAULT_MATERIALS;
};

const Chips = ({ label, options, value, onChange, testId }) => (
  <div>
    <p className="eyebrow !text-[10px] text-slate-400">{label}</p>
    <div className="mt-3 flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          data-testid={`${testId}-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
          onClick={() => onChange(o)}
          className={`border px-3.5 py-2 text-xs font-semibold uppercase tracking-wider transition-[background-color,border-color,color] duration-200 ${
            value === o ? "border-[#0a6cff] bg-[#0a6cff] text-white" : "border-slate-700 text-slate-300 hover:border-sky-400 hover:text-white"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

export const QuoteConfigurator = ({ product }) => {
  const { open } = useRfq();
  const materials = useMemo(() => parseMaterials(product.specs), [product.specs]);
  const [mat, setMat] = useState(materials[0]);
  const [hard, setHard] = useState(70);
  const [vol, setVol] = useState(VOLUMES[1]);
  const [colour, setColour] = useState(COLOURS[0]);

  const summary = [
    ["Material", mat],
    ["Hardness", `${hard} ± 5 Shore A`],
    ["Volume", `${vol} pcs / month`],
    ["Colour", colour],
  ];
  const send = () =>
    open(product.name, product.id, {
      quantity: `${vol} pcs / month`,
      message: `Configured spec:\n${summary.map(([k, v]) => `- ${k}: ${v}`).join("\n")}`,
    });

  return (
    <div data-testid="quote-configurator" className="clip-corner mt-12 border border-slate-800 bg-[#0d121b] p-6 sm:p-8">
      <div className="flex items-center justify-between gap-4">
        <p className="eyebrow flex items-center gap-2 text-sky-400"><SlidersHorizontal className="h-4 w-4" /> Instant quote builder</p>
        <span className="font-mono-x text-[10px] uppercase tracking-widest text-slate-400">No pricing shown · RFQ</span>
      </div>
      <div className="mt-7 space-y-7">
        <Chips label="Material / compound" options={materials} value={mat} onChange={setMat} testId="config-material" />
        <div>
          <div className="flex items-end justify-between">
            <p className="eyebrow !text-[10px] text-slate-400">Hardness (Shore A)</p>
            <p data-testid="config-hardness-value" className="font-display text-3xl font-extrabold text-white">{hard}<span className="ml-1 text-base text-slate-400">ShA</span></p>
          </div>
          <Slider data-testid="config-hardness-slider" className="mt-4" min={30} max={90} step={5} value={[hard]} onValueChange={(v) => setHard(v[0])} />
          <div className="font-mono-x mt-2 flex justify-between text-[10px] text-slate-400"><span>30 · soft</span><span>60</span><span>90 · hard</span></div>
        </div>
        <Chips label="Monthly volume (pcs)" options={VOLUMES} value={vol} onChange={setVol} testId="config-volume" />
        <Chips label="Colour" options={COLOURS} value={colour} onChange={setColour} testId="config-colour" />
      </div>
      <div className="mt-8 grid grid-cols-2 gap-px bg-slate-800 sm:grid-cols-4" data-testid="config-summary">
        {summary.map(([k, v]) => (
          <div key={k} className="bg-[#07090e] p-3">
            <p className="font-mono-x text-[10px] uppercase tracking-widest text-slate-400">{k}</p>
            <p className="mt-1 text-sm font-semibold text-white">{v}</p>
          </div>
        ))}
      </div>
      <button data-testid="config-send-rfq" onClick={send} className="btn-electric mt-6 w-full">
        Send this spec for a quote <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
};
